// https://docs.sentry.io/platforms/javascript/user-feedback/configuration/#bring-your-own-button

import * as Sentry from "@sentry/react";
import styles from "./AISupportSentryFeedback.module.scss";
import ThumbUpIcon from "@medoma/components/icons/ThumbUpIcon";
import ThumbDownIcon from "@medoma/components/icons/ThumbDownIcon";
import { IconButton } from "@medoma/components/Button/Button";
import { Text } from "@medoma/components/Text/Text";
import { Trans, useLingui } from "@lingui/react/macro";

export default function AISupportSentryFeedback({
  sessionId,
  feedbackSent,
  setFeedbackSent,
}: {
  sessionId: string | null;
  feedbackSent: boolean;
  setFeedbackSent: (feedbackSent: boolean) => void;
}) {
  const { t } = useLingui();
  if (feedbackSent) {
    return (
      <Text element="div" color="faded" size="small">
        <Trans>Tack för din feedback!</Trans>
      </Text>
    );
  }

  async function sendFeedback(feedbackType: "bad" | "good") {
    const feedback = {
      name: "AI Support Feedback",
      message: feedbackType === "bad" ? t`Dåligt svar` : t`Bra svar`,
      tags: {
        type: "ai-support-agent", // Used to filter feedback events to activate correct Slack channel integration.
      },
    };

    const hint = {
      captureContext: {
        tags: {
          "conversation.session_id": sessionId,
        },
        contexts: {
          conversationDetails: {
            sessionId,
          },
        },
      },
    };

    // Using captureFeedback instead of sendFeedback since it is not critical that the feedback is successfully received
    Sentry.captureFeedback(feedback, hint);

    // Callback to update the feedbackSent state in the parent component
    setFeedbackSent(true);
  }

  return (
    <div className={styles.feedbackButtons}>
      <Text element="div" color="faded" size="small">
        <Trans>Fick du svar på din fråga?</Trans>
      </Text>
      <IconButton
        aria-label={t`Nej`}
        onClick={async () => await sendFeedback("bad")}
      >
        <ThumbDownIcon />
      </IconButton>
      <IconButton
        aria-label={t`Ja`}
        onClick={async () => await sendFeedback("good")}
      >
        <ThumbUpIcon />
      </IconButton>
    </div>
  );
}
